import { ref } from 'vue'
import { defineStore } from 'pinia'
import { collection, onSnapshot, query, where, type Unsubscribe } from 'firebase/firestore'
import { db } from '@/firebase'
import type { RosterMember } from '@/types'

// Agency roster (imported from D4H). Read-only on the client; the backend sync writes it.
// The snapshot listener keeps it in the offline cache so check-in lookup works offline.
export const useRosterStore = defineStore('roster', () => {
  const members = ref<RosterMember[]>([])
  const loading = ref(false)

  let unsubFn: Unsubscribe | null = null
  let _agencyId = ''

  function subscribe(agencyId: string) {
    if (agencyId === _agencyId && unsubFn) return
    unsubFn?.()
    _agencyId = agencyId
    loading.value = true
    const q = query(collection(db, 'agencies', agencyId, 'roster'), where('active', '==', true))
    unsubFn = onSnapshot(q, (snap) => {
      members.value = snap.docs
        .map((d) => ({ roster_id: d.id, ...d.data() }) as RosterMember)
        .sort((a, b) => a.last.localeCompare(b.last) || a.first.localeCompare(b.first))
      loading.value = false
    }, () => {
      loading.value = false
    })
  }

  function search(term: string, limit = 8): RosterMember[] {
    const needle = term.trim().toLowerCase()
    if (needle.length < 2) return []
    return members.value
      .filter((m) =>
        `${m.first} ${m.last}`.toLowerCase().includes(needle)
        || `${m.last} ${m.first}`.toLowerCase().includes(needle)
        || (m.nickname ?? '').toLowerCase().includes(needle)
        || m.email.includes(needle),
      )
      .slice(0, limit)
  }

  function unsubscribe() {
    unsubFn?.()
    unsubFn = null
    _agencyId = ''
    members.value = []
  }

  return { members, loading, subscribe, search, unsubscribe }
})
